import * as React from "react";
import { useUpPromoteJoinEnabled } from "@/hooks/useUpPromoteJoinEnabled";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import {
  Search,
  ShoppingBag,
  Menu,
  X,
  ChevronDown,
  Check,
  User,
  Heart,
  Bell,
  BellOff,
  Package,
} from "lucide-react";
import { enablePush, disablePush, isPushEnabled } from "@/lib/push/client";
import { isPushAllowedContext } from "@/lib/push/guard";
import { lazyToast as toast } from "@/lib/lazy-toast";
import { useCartStore } from "@/stores/cartStore";
import { useLocale } from "@/contexts/LocaleContext";
import { NEW_PRODUCT_PATH } from "@/components/CollectionTabsNav";
import { useAuth } from "@/contexts/AuthContext";
import { useWishlist } from "@/hooks/useWishlist";
import { Locale, LOCALE_LABEL, VISIBLE_LOCALES } from "@/lib/i18n";
import { MARKET_LIST, MARKETS, MarketId } from "@/lib/markets";
import { SearchOverlay } from "@/components/SearchOverlay";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { InboxBell } from "@/components/InboxBell";
import { BrandWordmark } from "@/components/BrandWordmark";
import { MarketSwitchConfirmDialog } from "@/components/MarketSwitchConfirmDialog";

/** حلقة خفيفة حول الأيقونة تُظهر أنّ المستخدم في هذه الصفحة الآن. */
const ICON_ACTIVE = "bg-secondary text-foreground ring-1 ring-foreground/25";

function AccountButton() {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  const active = pathname === "/account" || pathname.startsWith("/account/");
  const className = cn(
    "relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-foreground/80 transition-colors hover:text-primary",
    active && ICON_ACTIVE,
  );
  const icon = (
    <>
      <User className="h-5 w-5" />
      {loading && (
        <span className="absolute bottom-0.5 end-0.5 h-2 w-2 rounded-full bg-muted-foreground/40 animate-pulse" />
      )}
    </>
  );
  // Always render a clickable Link — auth loading shouldn't block navigation
  const to = user || loading ? "/account" : "/auth";
  const search = user || loading ? undefined : { redirect: "/account", mode: "signin" as const };
  return (
    <Link
      to={to}
      search={search as any}
      className={className}
      aria-label="Account"
      aria-current={active ? "page" : undefined}
    >
      {icon}
    </Link>
  );
}

function TrackOrderButton() {
  const { t } = useLocale();
  const { pathname } = useLocation();
  const active = pathname === "/track-order";
  const label = t("nav.track_order");
  return (
    <Link
      to="/track-order"
      title={label}
      aria-label={label}
      aria-current={active ? "page" : undefined}
      className={cn(
        "relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-foreground/80 transition-colors hover:text-primary",
        active && ICON_ACTIVE,
      )}
    >
      <Package className="h-5 w-5" />
    </Link>
  );
}

function WishlistButton() {
  const { user } = useAuth();
  const { items } = useWishlist();
  const { pathname } = useLocation();
  const active = pathname.startsWith("/account/wishlist");
  const count = items.length;
  const to = user ? "/account/wishlist" : "/auth";
  const search = user
    ? undefined
    : ({ redirect: "/account/wishlist", mode: "signin" as const } as const);
  return (
    <Link
      to={to}
      search={search as any}
      className={cn(
        "relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-foreground/80 transition-colors hover:text-primary",
        active && ICON_ACTIVE,
      )}
      aria-label="Wishlist"
      aria-current={active ? "page" : undefined}
    >
      <Heart className="h-5 w-5" />
      {count > 0 && (
        <span
          aria-hidden
          className="absolute -end-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-background px-1 text-[10px] font-semibold leading-none text-rule-accent ring-1 ring-rule-accent/40"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </Link>
  );
}

export function Header() {
  const { pathname } = useLocation();
  const {
    t,
    locale,
    marketId,
    setLocale,
    setMarket,
    marketReady,
    currencyLabels,

    resetMarketToAuto,
    marketSource,
    dir,
    countryCode,
    effectiveCountryCode,
    giftMarketOverride,
    clearGiftMarketOverride,
  } = useLocale();
  const items = useCartStore((s) => s.items);
  const openCart = useCartStore((s) => s.openCart);
  const ensureMarket = useCartStore((s) => s.ensureMarket);
  const clearCart = useCartStore((s) => s.clearCart);
  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);

  // مبدّل السوق مُقفَل تماماً فقط أثناء جلسة إهداء (override نشط).
  // عند وجود عناصر في السلّة لا نقفله بل نعترض ونعرض Dialog يخيّر العميل.
  const cartLocksMarket = items.length > 0;
  const switcherLocked = !!giftMarketOverride;
  const giftMarket = giftMarketOverride ? MARKETS[giftMarketOverride] : null;

  // حالة Dialog طلب تبديل السوق مع سلّة غير فارغة
  const [pendingMarket, setPendingMarket] = React.useState<MarketId | null>(null);
  // طلب إعادة سلّة غير فارغة لسوق العميل: يمرّ بتأكيد + المعاملة المتسلسلة نفسها.
  const [returnMarket, setReturnMarket] = React.useState<MarketId | null>(null);
  const confirmReturnMarket = React.useCallback(async () => {
    const target = returnMarket;
    setReturnMarket(null);
    if (!target) return;
    const res = await useCartStore
      .getState()
      .switchCartMarketForGift(MARKETS[target].countryCode as never, { returnToShopperMarket: true });
    if (res.ok) {
      clearGiftMarketOverride();
      return;
    }
    const key =
      res.reason === "uncertain"
        ? "gift.market_switch_uncertain"
        : res.reason === "unavailable" && res.titles?.length
          ? "gift.market_switch_unavailable"
          : "gift.market_switch_failed";
    toast.error(t(key, { items: res.titles?.join(" · ") ?? "" }));
  }, [returnMarket, clearGiftMarketOverride, t]);

  /** Wrapper: يعترض تبديل السوق إذا السلّة غير فارغة ويفتح Dialog بدل التطبيق المباشر. */
  const requestSetMarket = React.useCallback(
    (m: MarketId) => {
      if (m === marketId) return;
      if (cartLocksMarket) {
        setPendingMarket(m);
        return;
      }
      setMarket(m);
    },
    [marketId, cartLocksMarket, setMarket],
  );

  React.useEffect(() => {
    // ننتظر انتهاء كشف السوق (IP + ملفّ العميل) قبل أيّ فحص تطابق.
    // بدون هذا: الافتراضيّ AE يُنشئ سلّة ثمّ يصل الكشف (مثلاً SA) فتُفرَغ
    // السلّة فوراً بعد الإضافة — وهذا ما كان يحدث على iPhone (شبكة أبطأ).
    if (!marketReady) return;
    ensureMarket(effectiveCountryCode);
  }, [marketReady, effectiveCountryCode, ensureMarket]);


  const [scrolled, setScrolled] = React.useState(false);
  // على الصفحة الرئيسيّة: الهيدر يندمج مع صورة الهيرو (شفّاف · نصّ أبيض) حتى أوّل تمرير.
  const isHome = pathname === "/";
  const overlay = isHome && !scrolled;
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [marketOpen, setMarketOpen] = React.useState(false);
  const [showCountryList, setShowCountryList] = React.useState(false);
  const [langOpen, setLangOpen] = React.useState(false);
  const [openMenu, setOpenMenu] = React.useState<string | null>(null);
  const marketWrapRef = React.useRef<HTMLDivElement>(null);
  const langWrapRef = React.useRef<HTMLDivElement>(null);
  const navMenuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!openMenu) return;
    const onDown = (e: MouseEvent) => {
      if (navMenuRef.current && !navMenuRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [openMenu]);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // إغلاق قوائم اللغة/السوق عند النقر خارجها (بدلاً من onMouseLeave الذي يُغلقها بحركة عابرة)
  React.useEffect(() => {
    if (!marketOpen && !langOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (marketOpen && marketWrapRef.current && !marketWrapRef.current.contains(t)) {
        setMarketOpen(false);
        setShowCountryList(false);
      }
      if (langOpen && langWrapRef.current && !langWrapRef.current.contains(t)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [marketOpen, langOpen]);

  type NavLink = {
    to: string;
    params?: Record<string, string>;
    label: string;
    children?: Array<{ to: string; params?: Record<string, string>; label: string }>;
  };
  // العناصر مشتقّة من مجموعات Shopify الحيّة فقط (single-perfume · gift-set-perfume-collection
  // · ladybag-collection · oud-scent). أيّ مجموعة محذوفة من شوبيفاي لا تظهر هنا.
  const upPromoteJoinOn = useUpPromoteJoinEnabled();
  const links: NavLink[] = [
    { to: "/", label: t("nav.home") },
    { to: "/shop", label: t("nav.shop") },
    { to: "/collection/$handle", params: { handle: "single-perfume" }, label: t("nav.single_perfume") },
    { to: "/collection/$handle", params: { handle: "oud-scent" }, label: t("nav.oud") },
    { to: "/collection/$handle", params: { handle: "gift-set-perfume-collection" }, label: t("nav.gift_sets") },
    { to: "/collection/$handle", params: { handle: "ladybag-collection" }, label: t("nav.lady_bag") },
    { to: NEW_PRODUCT_PATH, label: t("nav.new_product") },
  ];
  if (upPromoteJoinOn) links.push({ to: "/ambassadors", label: t("nav.ambassador_program") });

  const activeMarket = MARKETS[marketId];
  const activeLanguageLabel = LOCALE_LABEL[locale];

  const detectedLine =
    locale === "ar"
      ? `اكتشفنا أنك من ${activeMarket.labels.ar}.`
      : locale === "fr"
        ? `Détecté : vous êtes en ${activeMarket.labels.fr}.`
        : `We detected you're in ${activeMarket.labels.en}.`;
  const correctLabel =
    locale === "ar" ? "صحيح، تابع" : locale === "fr" ? "C'est correct" : "That's correct";
  const otherLabel =
    locale === "ar" ? "أنا من بلد آخر" : locale === "fr" ? "Je suis d'un autre pays" : "I'm from another country";
  const manualLabel =
    locale === "ar" ? "اختيار يدوي" : locale === "fr" ? "Choix manuel" : "Manual selection";
  const resetLabel =
    locale === "ar" ? "إعادة الكشف التلقائي" : locale === "fr" ? "Détection auto" : "Reset to auto-detect";
  const searchPlaceholder =
    locale === "ar"
      ? "ابحث عن عطر أو منتج"
      : locale === "fr"
        ? "Rechercher un parfum ou un produit"
        : "Search for a fragrance or product";

  const closeAllMenus = () => {
    setMarketOpen(false);
    setShowCountryList(false);
  };

  return (
    <>
      <header
        className={cn(
          "z-40 text-foreground transition-[background-color,box-shadow] duration-300",
          isHome ? "fixed inset-x-0 top-0" : "sticky top-0",
          overlay
            ? "border-b border-transparent bg-transparent [&_img]:brightness-0 [&_img]:invert"
            : "border-b border-foreground/90 bg-background",
          scrolled && !overlay && "shadow-[0_1px_0_rgba(0,0,0,0.04)]",
        )}
        style={
          overlay
            ? ({
                ["--foreground" as string]: "oklch(1 0 0)",
                ["--muted-foreground" as string]: "oklch(1 0 0 / 0.78)",
                ["--border" as string]: "oklch(1 0 0 / 0.35)",
                ["--primary" as string]: "oklch(1 0 0)",
                ["--popover" as string]: "oklch(0.16 0 0)",
                ["--popover-foreground" as string]: "oklch(1 0 0)",
              } as React.CSSProperties)
            : undefined
        }
        dir={dir}
      >
        {/* الشريط الرئيسيّ — موبايل: منيو يسار · الشعار وسط · أيقونات يمين. ديسكتوب: شعار يسار */}
        <div
          dir="ltr"
          className="mx-auto grid h-16 w-full min-w-0 max-w-7xl grid-cols-[1fr_auto_1fr] items-center gap-1 px-3 sm:px-4 md:flex md:h-20 md:justify-between md:gap-6 md:px-8"
        >
          {/* منيو موبايل — أقصى اليسار الفيزيائي */}
          <button
            className="md:hidden inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-foreground/80 transition-colors hover:text-primary"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* الوسط على الموبايل · اليسار على الديسكتوب: لوغو LECMO */}
          <Link
            to="/"
            className="inline-flex min-w-0 items-center justify-center leading-none text-foreground md:flex-none md:justify-start"
          >
            <BrandWordmark className="h-9 w-auto sm:h-11 md:h-12" />
          </Link>

          {/* اليمين الفيزيائي: بحث + Country + Lang + Account + Bell + Wishlist + Cart */}
          <div dir="ltr" className="flex shrink-0 items-center justify-end gap-0 sm:gap-px md:gap-2">
            <button
              onClick={() => setSearchOpen(true)}
              className="hidden md:flex max-w-[260px] items-center gap-2 border-b border-border/70 pb-1.5 me-2 text-start t-body-sm text-muted-foreground transition-colors hover:border-primary hover:text-foreground"
              aria-label={searchPlaceholder}
            >
              <Search className="h-4 w-4" />
              <span className="truncate">{searchPlaceholder}</span>
            </button>
            <button
              onClick={() => setSearchOpen(true)}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-foreground/80 transition-colors hover:text-primary md:hidden"
              aria-label="Search"
            >
              <Search className="h-5 w-5" />
            </button>




            {/* شارة جلسة الإهداء — تظهر بدل/فوق مبدّل السوق عند override */}
            {giftMarket && (
              <div className="hidden md:flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-2.5 py-1 t-eyebrow uppercase text-primary">
                <span className="text-sm leading-none">🎁</span>
                <span>
                  {t("gift.active_indicator", {
                    country: `${giftMarket.flag} ${giftMarket.labels[locale]}`,
                  })}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    // سلّة غير فارغة: العودة = إعادة تسعير مؤكَّدة، لا إفراغ.
                    if (useCartStore.getState().items.length > 0) setReturnMarket(marketId);
                    else clearGiftMarketOverride();
                  }}
                  className="ms-1 text-primary/70 transition-colors hover:text-primary"
                  aria-label={t("gift.cancel_override")}
                  title={t("gift.cancel_override")}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}

            {/* Country chip — ديسكتوب فقط — مُقفَل عند override أو سلّة غير فارغة */}
            <div className="relative hidden md:block" ref={marketWrapRef}>
              <button
                data-market-chip
                onClick={() => {
                  if (switcherLocked) return;
                  setMarketOpen((o) => !o);
                  setShowCountryList(false);
                  setLangOpen(false);
                }}
                disabled={switcherLocked}
                className={cn(
                  "flex items-center gap-1.5 rounded-full px-2 py-2 text-xs uppercase tracking-[var(--tracking-wide)] transition-colors",
                  switcherLocked
                    ? "cursor-not-allowed text-muted-foreground/60"
                    : "text-foreground/80 hover:text-primary",
                )}
                aria-label="Change market"
                title={
                  switcherLocked
                    ? giftMarketOverride
                      ? t("market.switcher_gift_active", {
                          country: giftMarket?.labels[locale] ?? "",
                        })
                      : t("market.switcher_locked_cart")
                    : undefined
                }
              >
                <span className="text-base leading-none">{activeMarket.flag}</span>
                <span>{currencyLabels[activeMarket.countryCode] || activeMarket.countryCode}</span>
                <ChevronDown className="h-3 w-3" />
              </button>
              {marketOpen && !switcherLocked && (
                <div dir={dir} className="absolute end-0 z-50 mt-2 w-80 rounded-none border border-border bg-popover p-3  animate-fade-up">
                  {!showCountryList ? (
                    <>
                      <p className="mb-2 text-xs text-foreground">
                        <span className="me-1.5 text-base leading-none">{activeMarket.flag}</span>
                        {detectedLine}
                      </p>
                      <p className="mb-3 t-eyebrow text-muted-foreground">
                        {currencyLabels[activeMarket.countryCode]
                          ? t("header.contextual_currency", { currency: currencyLabels[activeMarket.countryCode] })
                          : t("header.currency_pending")}
                      </p>
                      {marketId === "ROW" && (
                        <div className="mb-3 rounded border border-amber-500/40 bg-amber-50/60 px-2.5 py-2 t-eyebrow leading-relaxed text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                          🌍{" "}
                          {locale === "ar"
                            ? "نخدم حالياً السعودية · الإمارات · العراق · عُمان فقط. الأسعار بالدولار للعرض."
                            : locale === "fr"
                              ? "Livraison uniquement en Arabie Saoudite · Émirats · Irak · Oman. Prix en USD à titre indicatif."
                              : "We currently ship only to Saudi Arabia · UAE · Iraq · Oman. Prices in USD for reference."}
                        </div>
                      )}
                      <div className="grid gap-1">
                        <button
                          onClick={closeAllMenus}
                          className="flex items-center justify-between rounded bg-primary px-3 py-2 text-xs text-primary-foreground"
                        >
                          <span>{correctLabel}</span>
                          <Check className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => setShowCountryList(true)}
                          className="rounded border border-border px-3 py-2 text-start text-xs text-foreground hover:bg-secondary"
                        >
                          {otherLabel}
                        </button>
                        {marketSource === "manual" && (
                          <button
                            onClick={async () => {
                              await resetMarketToAuto();
                              closeAllMenus();
                            }}
                            className="rounded px-3 py-2 text-start t-eyebrow text-muted-foreground hover:text-primary"
                          >
                            ↻ {resetLabel}
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="mb-2 t-micro uppercase tracking-[var(--tracking-wide)] text-muted-foreground">
                        {manualLabel}
                      </p>
                      <div className="max-h-72 overflow-y-auto pe-1">
                        {MARKET_LIST.map((m) => (
                          <button
                            key={m.id}
                            onClick={() => {
                              requestSetMarket(m.id);
                              closeAllMenus();
                            }}
                            className={cn(
                              "flex w-full items-center justify-between rounded px-2 py-1.5 text-xs transition-colors",
                              marketId === m.id
                                ? "bg-primary text-primary-foreground"
                                : "text-foreground hover:bg-secondary",
                            )}
                          >
                            <span className="flex items-center gap-2">
                              <span className="text-base leading-none">{m.flag}</span>
                              <span>{m.labels[locale]}</span>
                              {m.id === "ROW" && (
                                <span
                                  className="ms-1 inline-flex h-1.5 w-1.5 rounded-full bg-amber-500"
                                  title={
                                    locale === "ar"
                                      ? "عرض فقط — لا شحن"
                                      : locale === "fr"
                                        ? "Affichage uniquement — pas d'expédition"
                                        : "View-only — no shipping"
                                  }
                                />
                              )}
                            </span>
                            <span className="t-micro opacity-80">{currencyLabels[m.countryCode] || t("header.currency_pending")}</span>
                          </button>
                        ))}
                      </div>
                      <p className="mt-2 px-1 t-micro leading-relaxed text-muted-foreground">
                        {t("header.country_shipping_note")}{" "}
                        <Link
                          to="/shipping"
                          onClick={() => setMarketOpen(false)}
                          className="underline underline-offset-2 hover:text-foreground"
                        >
                          {t("header.country_shipping_link")}
                        </Link>
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Lang — ديسكتوب فقط */}
            <div className="relative hidden md:block" ref={langWrapRef}>
              <button
                onClick={() => {
                  const next = VISIBLE_LOCALES.find((l) => l !== locale) ?? locale;
                  setLocale(next);
                  setLangOpen(false);
                  setMarketOpen(false);
                }}
                className="inline-flex items-center gap-2 rounded-full border border-border/70 px-3 py-2 text-foreground transition-colors hover:border-primary/50 hover:text-primary"
                aria-label="Change language"
              >
                <span className="t-micro uppercase text-muted-foreground">{t("account.row.language")}</span>
                <span className="text-xs">{LOCALE_LABEL[VISIBLE_LOCALES.find((l) => l !== locale) ?? locale]}</span>
                <span className="t-micro uppercase text-primary">{(VISIBLE_LOCALES.find((l) => l !== locale) ?? locale).toUpperCase()}</span>
              </button>

              {langOpen && (
                <div dir={dir} className="absolute end-0 z-50 mt-2 w-40 rounded-none border border-border bg-popover p-2  animate-fade-up">
                  {VISIBLE_LOCALES.map((l) => (
                    <button
                      key={l}
                      onClick={() => {
                        setLocale(l);
                        setLangOpen(false);
                      }}
                      className={cn(
                        "flex w-full items-center justify-between rounded px-2 py-1.5 text-xs transition-colors",
                        locale === l
                          ? "bg-primary text-primary-foreground"
                          : "text-foreground hover:bg-secondary",
                      )}
                    >
                      <span>{LOCALE_LABEL[l]}</span>
                      <span className="inline-flex items-center gap-1">
                        {locale === l && <Check className="h-3.5 w-3.5" />}
                        <span className="t-micro opacity-70">{l.toUpperCase()}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <span className="hidden md:inline-flex">
              <TrackOrderButton />
            </span>
            <span className="hidden md:inline-flex"><AccountButton /></span>
            <InboxBell />
            <span className="hidden md:inline-flex"><WishlistButton /></span>

            {/* السلّة على الديسكتوب فقط — على الموبايل موجودة في الناف بار السفليّ */}
            <span className="hidden md:inline-flex">
            <button
              type="button"
              onClick={openCart}
              className="relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-foreground/80 transition-colors hover:text-primary md:h-11 md:w-11"
              aria-label="Cart"
            >
              <ShoppingBag className="h-5 w-5" />
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -end-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-background px-1 t-micro font-medium text-rule-accent ring-1 ring-rule-accent/40">
                  {totalItems}
                </span>
              )}
            </button>
            </span>




          </div>
        </div>

        {/* الشريط الثاني — روابط التنقّل (ديسكتوب فقط) — Nemro thin underline nav */}
        <nav className="hidden md:block border-t border-foreground/15">
          <div className="mx-auto flex h-11 max-w-7xl items-center justify-center gap-4 px-4 lg:gap-6 lg:px-6 xl:gap-8">
            {links.map((l) =>
              l.children ? (
                (() => {
                  const parentActive = l.children.some((c) =>
                    c.params?.handle
                      ? pathname === `/collection/${c.params.handle}`
                      : pathname === c.to,
                  );
                  return (
                <div key={l.label} className="relative" ref={openMenu === l.label ? navMenuRef : undefined}>
                  <button
                    type="button"
                    onClick={() => setOpenMenu((p) => (p === l.label ? null : l.label))}
                    aria-expanded={openMenu === l.label}
                    className={cn(
                      "relative inline-flex items-center gap-1 whitespace-nowrap text-[11px] uppercase tracking-[0.16em] transition-colors duration-200 hover:text-foreground",
                      parentActive ? "text-foreground" : "text-foreground/85",
                    )}
                  >
                    {l.label}
                    <ChevronDown className={cn("h-3 w-3 transition-transform duration-200", openMenu === l.label && "rotate-180")} />
                    {parentActive && (
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-x-0 -bottom-[14px] h-px bg-foreground"
                      />
                    )}
                  </button>
                  {openMenu === l.label && (
                    <div className="absolute left-1/2 top-full z-50 mt-0 min-w-[220px] -translate-x-1/2 border border-foreground bg-background p-4 flex flex-col gap-3 animate-fade-up">
                      {l.children.map((c) => (
                        <Link
                          key={c.params?.handle ?? c.to}
                          to={c.to as any}
                          params={c.params as any}
                          onClick={() => setOpenMenu(null)}
                          className="block text-[12px] text-foreground/85 transition-colors hover:text-foreground"
                          activeProps={{ className: "text-foreground font-medium" }}
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
                  );
                })()
              ) : (
                <Link
                  key={l.params?.handle ?? l.to}
                  to={l.to as any}
                  params={l.params as any}
                  className="relative whitespace-nowrap text-[11px] uppercase tracking-[0.16em] text-foreground/85 transition-colors duration-200 hover:text-foreground"
                  activeProps={{
                    className: "text-foreground [&>span]:scale-x-100",
                  }}
                >
                  {l.label}
                  <span className="pointer-events-none absolute inset-x-0 -bottom-[14px] h-px origin-center scale-x-0 bg-foreground transition-transform duration-200" />
                </Link>
              ),
            )}
          </div>
        </nav>
      </header>

      {mobileOpen && (
        <MobileNav
          links={links}
          onClose={() => setMobileOpen(false)}
          locale={locale}
          marketId={marketId}
          setLocale={setLocale}
          setMarket={requestSetMarket}
        />
      )}

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      <MarketSwitchConfirmDialog
        open={!!returnMarket}
        to={returnMarket}
        onConfirm={() => void confirmReturnMarket()}
        onCancel={() => setReturnMarket(null)}
      />

      {pendingMarket && (() => {
        const currentMkt = MARKETS[marketId];
        const targetMkt = MARKETS[pendingMarket];
        const currentLabel = `${currentMkt.flag} ${currentMkt.labels[locale]}`;
        const targetLabel = `${targetMkt.flag} ${targetMkt.labels[locale]}`;
        return (
          <AlertDialog
            open={!!pendingMarket}
            onOpenChange={(o) => (!o ? setPendingMarket(null) : undefined)}
          >
            <AlertDialogContent className="z-[70]">
              <AlertDialogHeader>
                <AlertDialogTitle>{t("cart.market_switch_title")}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t("cart.market_switch_body", {
                    current: currentLabel,
                    currentCurrency: currencyLabels[currentMkt.countryCode] || t("header.currency_pending"),
                    target: targetLabel,
                    targetCurrency: currencyLabels[targetMkt.countryCode] || t("header.currency_pending"),
                  })}
                </AlertDialogDescription>
                <p className="t-caption text-muted-foreground mt-2">
                  {t("cart.market_switch_hint")}
                </p>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setPendingMarket(null)}>
                  {t("cart.market_switch_keep")}
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    clearCart();
                    setMarket(pendingMarket);
                    setPendingMarket(null);
                  }}
                >
                  {t("cart.market_switch_clear", { target: targetMkt.labels[locale] })}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        );
      })()}
    </>
  );
}

function MobileNav({
  links,
  onClose,
  locale,
  marketId,
  setLocale,
  setMarket,
}: {
  links: Array<{
    to: string;
    params?: Record<string, string>;
    label: string;
    children?: Array<{ to: string; params?: Record<string, string>; label: string }>;
  }>;
  onClose: () => void;
  locale: Locale;
  marketId: MarketId;
  setLocale: (l: Locale) => void;
  setMarket: (m: MarketId) => void;
}) {
  const { t, currencyLabels } = useLocale();
  const navigate = useNavigate();
  const [countryOpen, setCountryOpen] = React.useState(false);
  const activeMarket = MARKETS[marketId];
  const closeBtnRef = React.useRef<HTMLButtonElement>(null);

  // إشعارات الجهاز (Web Push)
  const pushSupported = React.useMemo(() => isPushAllowedContext(), []);
  const [pushOn, setPushOn] = React.useState(false);
  const [pushBusy, setPushBusy] = React.useState(false);
  React.useEffect(() => {
    if (!pushSupported) return;
    isPushEnabled().then(setPushOn).catch(() => {});
  }, [pushSupported]);
  const togglePush = async () => {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      if (pushOn) {
        await disablePush();
        setPushOn(false);
        toast.success(locale === "ar" ? "تمّ إيقاف الإشعارات." : "Notifications disabled.");
      } else {
        const res = await enablePush(locale === "ar" ? "ar" : "en");
        if (res.ok) {
          setPushOn(true);
          toast.success(locale === "ar" ? "تمّ تفعيل الإشعارات." : "Notifications enabled.");
        } else if (res.reason === "denied") {
          toast.error(locale === "ar" ? "السماح مرفوض من المتصفّح." : "Permission denied in browser.");
        } else if (res.reason === "ios-install") {
          toast.message(locale === "ar" ? "أضف الموقع للشاشة الرئيسيّة أوّلاً." : "Add to Home Screen first.");
        } else {
          toast.error(locale === "ar" ? "تعذّر التفعيل." : "Could not enable.");
        }
      }
    } finally {
      setPushBusy(false);
    }
  };

  // ESC للإغلاق + قفل تمرير الخلفيّة + تركيز ابتدائي
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeBtnRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label={locale === "ar" ? "إغلاق القائمة" : "Close menu"}
        onClick={onClose}
        className="absolute inset-0 animate-fade-up"
        style={{ animationDuration: "180ms" }}
      />

      <aside
        className="absolute left-1/2 -translate-x-1/2 top-[calc(max(env(safe-area-inset-top),12px)+64px)] bottom-[max(env(safe-area-inset-bottom),12px)] flex w-[290px] max-w-[calc(100vw-24px)] flex-col overflow-hidden rounded-[28px] border border-white/40 bg-white/10 text-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.55),0_24px_70px_-24px_rgba(0,0,0,0.28)] backdrop-blur-[28px] backdrop-saturate-150 animate-slide-in-right dark:bg-white/[0.04] dark:border-white/10 [scrollbar-width:none] [&_*]:[scrollbar-width:none] [&_*::-webkit-scrollbar]:hidden"
        style={{ animationDuration: "240ms" }}
      >
        {/* Header — زرّ إغلاق فقط */}
        <div className="flex h-12 shrink-0 items-center justify-end px-3">
          <button
            ref={closeBtnRef}
            onClick={onClose}
            aria-label={locale === "ar" ? "إغلاق القائمة" : "Close menu"}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Nav links */}
        <nav className="flex-1 overflow-y-auto px-4 py-5">
          <ul className="flex flex-col gap-2">
            {links.map((l) => (
              <li key={l.label}>
                <button
                  onClick={() => {
                    navigate({ to: l.to as any, params: l.params as any });
                    onClose();
                  }}
                  className="flex min-h-[40px] w-full items-center rounded-none border border-white/50 bg-white/50 px-5 text-start font-sans t-body-sm font-light uppercase tracking-wide leading-tight text-foreground/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)] backdrop-blur-md transition-all duration-[180ms] ease-out hover:bg-white/70 hover:border-white/70 active:scale-[0.99] dark:bg-white/[0.04] dark:border-white/10 dark:hover:bg-white/[0.08]"
                >
                  {l.label}
                </button>
                {l.children && (
                  <ul className="mt-1.5 flex flex-col gap-1.5 ps-4">
                    {l.children.map((c) => (
                      <li key={c.params?.handle ?? c.to}>
                        <button
                          onClick={() => {
                            navigate({ to: c.to as any, params: c.params as any });
                            onClose();
                          }}
                          className="flex min-h-[36px] w-full items-center rounded-none border border-white/40 bg-white/30 px-4 text-start t-caption font-light text-foreground/85 backdrop-blur-md transition-all hover:bg-white/50"
                        >
                          {c.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
            <li>
              <button
                onClick={() => {
                  navigate({ to: "/account" });
                  onClose();
                }}
                className="flex min-h-[40px] w-full items-center rounded-none border border-white/50 bg-white/50 px-5 text-start font-sans t-body-sm font-light uppercase tracking-wide leading-tight text-foreground/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)] backdrop-blur-md transition-all duration-[180ms] ease-out hover:bg-white/70 hover:border-white/70 active:scale-[0.99] dark:bg-white/[0.04] dark:border-white/10 dark:hover:bg-white/[0.08]"
              >
                {t("nav.account")}
              </button>
            </li>
            <li>
              <button
                onClick={() => {
                  navigate({ to: "/track-order" });
                  onClose();
                }}
                className="flex min-h-[40px] w-full items-center rounded-none border border-white/50 bg-white/50 px-5 text-start font-sans t-body-sm font-light uppercase tracking-wide leading-tight text-foreground/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)] backdrop-blur-md transition-all duration-[180ms] ease-out hover:bg-white/70 hover:border-white/70 active:scale-[0.99] dark:bg-white/[0.04] dark:border-white/10 dark:hover:bg-white/[0.08]"
              >
                {t("nav.track_order")}
              </button>
            </li>
            <li>
              <button
                onClick={() => {
                  navigate({ to: "/account/media" });
                  onClose();
                }}
                className="flex min-h-[40px] w-full items-center rounded-none border border-white/50 bg-white/50 px-5 text-start font-sans t-body-sm font-light uppercase tracking-wide leading-tight text-foreground/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)] backdrop-blur-md transition-all duration-[180ms] ease-out hover:bg-white/70 hover:border-white/70 active:scale-[0.99] dark:bg-white/[0.04] dark:border-white/10 dark:hover:bg-white/[0.08]"
              >
                {t("nav.media_library")}
              </button>
            </li>
          </ul>
        </nav>

        {/* Sticky bottom controls — glassy, seamless with navbar */}
        <div className="shrink-0 px-4 pb-3 pt-2">
          <button
            type="button"
            onClick={() => setCountryOpen((o) => !o)}
            className="flex min-h-[40px] w-full items-center justify-between rounded-full border border-white/40 bg-white/25 px-4 text-start shadow-[inset_0_1px_0_rgba(255,255,255,0.5)] backdrop-blur-md backdrop-saturate-150 transition-colors hover:bg-white/40 dark:bg-white/[0.06] dark:border-white/15"
            aria-expanded={countryOpen}
          >
            <span className="flex items-center gap-2 text-[13px] text-foreground">
              <span className="text-base leading-none">{activeMarket.flag}</span>
              <span>{activeMarket.labels[locale]}</span>
              <span className="text-xs text-muted-foreground">· {currencyLabels[activeMarket.countryCode] || t("header.currency_pending")}</span>
            </span>
            <ChevronDown
              className={cn(
                "h-4 w-4 text-muted-foreground transition-transform",
                countryOpen && "rotate-180",
              )}
            />
          </button>

          {countryOpen && (
            <>
              <div className="mt-3 grid max-h-56 gap-2 overflow-y-auto pe-1">
                {MARKET_LIST.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => {
                      setMarket(m.id);
                      setCountryOpen(false);
                    }}
                    className={cn(
                      "flex min-h-[40px] items-center justify-between rounded-none border px-3 py-2 text-sm transition-colors backdrop-blur-md",
                      marketId === m.id
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-white/30 bg-white/20 text-foreground hover:bg-white/40 dark:bg-white/[0.06] dark:border-white/15",
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-base">{m.flag}</span>
                      <span>{m.labels[locale]}</span>
                      {m.id === "ROW" && (
                        <span className="ms-1 inline-flex h-1.5 w-1.5 rounded-full bg-amber-500" />
                      )}
                    </span>
                    <span className="text-xs opacity-80">{currencyLabels[m.countryCode] || t("header.currency_pending")}</span>
                  </button>
                ))}
              </div>
              {marketId === "ROW" && (
                <div className="mt-2 rounded border border-amber-500/40 bg-amber-50/60 px-2.5 py-2 t-eyebrow leading-relaxed text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                  🌍{" "}
                  {locale === "ar"
                    ? "نخدم حالياً السعودية · الإمارات · العراق · عُمان فقط."
                    : locale === "fr"
                      ? "Livraison uniquement en Arabie Saoudite · Émirats · Irak · Oman."
                      : "We currently ship only to Saudi Arabia · UAE · Iraq · Oman."}
                </div>
              )}
            </>
          )}

          <div className="mt-2 flex items-center justify-between px-1">
            <span className="t-micro uppercase text-muted-foreground">{t("account.row.language")}</span>
            <span className="text-[12px] text-foreground">{LOCALE_LABEL[locale]}</span>
          </div>

          {/* Segmented language control — glassy */}
          <div className="mt-1.5 inline-flex w-full rounded-full border border-white/40 bg-white/20 p-0.5 backdrop-blur-md backdrop-saturate-150 dark:bg-white/[0.05] dark:border-white/15">
            {VISIBLE_LOCALES.map((l) => (
              <button
                key={l}
                onClick={() => setLocale(l)}
                className={cn(
                  "flex-1 min-h-[32px] rounded-full px-3 text-[13px] transition-all duration-[180ms]",
                  locale === l
                    ? "bg-white/70 text-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.6)] backdrop-blur-md dark:bg-white/15"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <span className="inline-flex items-center gap-1.5">
                  {locale === l && <Check className="h-3.5 w-3.5" />}
                  <span>{LOCALE_LABEL[l]}</span>
                </span>
              </button>
            ))}
          </div>

          {/* Web Push toggle */}
          {pushSupported && (
            <button
              type="button"
              onClick={togglePush}
              disabled={pushBusy}
              aria-pressed={pushOn}
              className={cn(
                "mt-2 flex min-h-[40px] w-full items-center justify-between rounded-full border px-4 backdrop-blur-md backdrop-saturate-150 transition-colors disabled:opacity-60",
                pushOn
                  ? "border-primary/40 bg-primary/15 hover:bg-primary/20"
                  : "border-white/40 bg-white/25 hover:bg-white/40 dark:bg-white/[0.06] dark:border-white/15",
              )}
            >
              <span className="flex items-center gap-2 text-[13px] text-foreground">
                {pushOn ? <Bell className="h-4 w-4 text-primary" /> : <BellOff className="h-4 w-4 text-muted-foreground" />}
                <span>{locale === "ar" ? "إشعارات الجهاز" : "Device notifications"}</span>
              </span>
              <span
                className={cn(
                  "relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors",
                  pushOn ? "bg-primary" : "bg-muted-foreground/40",
                )}
              >
                <span
                  className={cn(
                    "absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all",
                    pushOn ? "start-[18px]" : "start-0.5",
                  )}
                />
              </span>
            </button>
          )}
        </div>
      </aside>
    </div>
  );
}
